// signaling.js — Firestore-based signaling for Peerly
// Room creation, joining, presence heartbeat, candidate management, teardown.
// Rooms use status flags + presence heartbeat TTL for cooperative lifecycle management.
// Room states: 'waiting' → 'active' → 'ended' (with 'stale' inferred from presence heartbeat age).
//
// Heartbeat is stored in a separate presence doc (rooms/{roomId}/presence/owner)
// to avoid triggering onSnapshot listeners on the room document, which would
// cause unnecessary reads and burn through Firestore quota.

import { ROOM } from './config.js';
import { getMutableState, Role } from './state.js';

// Module-level ICE candidate batching state
const _candidateBatch = [];
let _candidateBatchTimer = null;
let _candidateBatchRole = null;
let _candidateBatchRoomId = null;

let db = null;

/**
 * Initialize the signaling module with a Firestore instance.
 */
export function init(firestoreDb) {
  db = firestoreDb;
}

/**
 * Check if a room exists and its current status.
 * Returns { exists, status, isStale, data, ref }
 */
export async function checkRoom(roomId) {
  const ref = db.collection('rooms').doc(roomId);
  const snap = await ref.get();

  if (!snap.exists) {
    return { exists: false, status: null, isStale: false, data: null, ref };
  }

  const data = snap.data();
  let isStale = false;
  const status = data.status || 'unknown';

  // Fast path: if createdAt is within stale threshold, skip presence read
  const createdAt = data.createdAt?.toDate?.();
  if (createdAt && Date.now() - createdAt.getTime() < ROOM.STALE_THRESHOLD_MS) {
    return { exists: true, status, isStale: false, data, ref };
  }

  // Room is older than threshold — check presence heartbeat for staleness
  try {
    const presenceRef = ref.collection('presence').doc('owner');
    const presenceSnap = await presenceRef.get();
    let heartbeat = null;
    if (presenceSnap.exists) {
      heartbeat = presenceSnap.data().timestamp?.toDate?.();
    }
    // Fall back to room doc heartbeat/createdAt for backward compat with old rooms
    if (!heartbeat) {
      heartbeat = data.heartbeat?.toDate?.() || createdAt;
    }
    if (heartbeat) {
      const age = Date.now() - heartbeat.getTime();
      isStale = age > ROOM.STALE_THRESHOLD_MS;
    } else {
      // No timestamp at all — treat as stale
      isStale = true;
    }
  } catch {
    isStale = true;
  }

  return { exists: true, status, isStale, data, ref };
}

/**
 * Delete all documents in a Firestore subcollection.
 */
async function deleteSubcollection(roomRef, subcollName) {
  try {
    const snap = await roomRef.collection(subcollName).get();
    const deletes = [];
    snap.forEach((doc) => deletes.push(doc.ref.delete()));
    await Promise.all(deletes);
  } catch (err) {
    console.warn(`[signaling] Failed to delete ${subcollName}:`, err);
  }
}

/**
 * Delete the presence subcollection (single doc: owner).
 */
async function deletePresence(roomRef) {
  try {
    await roomRef.collection('presence').doc('owner').delete();
  } catch (err) {
    console.warn('[signaling] Failed to delete presence:', err);
  }
}

/**
 * Clean up stale room data (candidates + doc) before creating a fresh room.
 */
async function cleanupStaleRoom(roomRef) {
  await Promise.all([
    deleteSubcollection(roomRef, 'callerCandidates'),
    deleteSubcollection(roomRef, 'calleeCandidates'),
    deletePresence(roomRef),
  ]);
  try {
    await roomRef.delete();
  } catch (err) {
    console.warn('[signaling] Failed to delete stale room doc:', err);
  }
}

/**
 * Force-overwrite a room: delete candidates and the room doc.
 * Exposed for the orchestrator to call when user confirms overwrite of an active room.
 */
export async function forceOverwriteRoom(roomId) {
  const roomRef = db.collection('rooms').doc(roomId);
  await cleanupStaleRoom(roomRef);
}

/**
 * Create a room (sender flow).
 * Sets offer, status, and heartbeat timestamps.
 * The ownerSessionKey is a cooperative hint — not a security boundary.
 *
 * Returns { success, error? }
 */
export async function createRoom(roomId, offer) {
  const roomRef = db.collection('rooms').doc(roomId);
  const room = await checkRoom(roomId);

  if (room.exists && !room.isStale) {
    // Room appears active — caller must confirm overwrite (handled by caller)
    return { success: false, error: 'room_active', room };
  }

  // Clean any stale leftovers
  if (room.exists) {
    await cleanupStaleRoom(roomRef);
  }

  // Create room doc + presence doc atomically via batch write
  const batch = db.batch();
  batch.set(roomRef, {
    offer: { type: offer.type, sdp: offer.sdp },
    status: 'waiting',
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    ownerSessionKey: generateOwnerKey(),
  });
  batch.set(roomRef.collection('presence').doc('owner'), {
    timestamp: firebase.firestore.FieldValue.serverTimestamp(),
  });
  await batch.commit();

  return { success: true };
}

/**
 * Join a room (receiver flow).
 * Validates room exists and has an offer.
 *
 * Returns { success, offer?, error? }
 */
export async function joinRoom(roomId) {
  const room = await checkRoom(roomId);

  if (!room.exists) {
    return { success: false, error: 'room_not_found' };
  }

  if (room.data.status === 'ended') {
    return { success: false, error: 'room_ended' };
  }

  if (room.data.status === 'active') {
    return { success: false, error: 'room_busy' };
  }

  if (!room.data.offer) {
    return { success: false, error: 'no_offer' };
  }

  // Mark room as active (1:1 — two participants max)
  try {
    await room.ref.update({
      status: 'active',
      guestJoinedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  } catch (err) {
    console.warn('[signaling] Failed to mark room active:', err);
  }

  return { success: true, offer: room.data.offer };
}

/**
 * Write the answer SDP to the room doc.
 */
export async function writeAnswer(roomId, answer) {
  await db
    .collection('rooms')
    .doc(roomId)
    .update({
      answer: { type: answer.type, sdp: answer.sdp },
      status: 'active',
    });
}

/**
 * Write a new offer SDP to the room doc (used for ICE restart).
 * Clears the old answer so the receiver knows to send a fresh one.
 */
export async function writeOffer(roomId, offer) {
  await db
    .collection('rooms')
    .doc(roomId)
    .update({
      offer: { type: offer.type, sdp: offer.sdp },
      answer: null,
    });
}

/**
 * Add an ICE candidate to the batching queue.
 * Candidates are flushed as a single batch document after ICE_CANDIDATE_BATCH_TIMEOUT_MS
 * or when iceGatheringState === 'complete' (via flushCandidateBatch).
 */
export function addCandidate(roomId, role, candidate) {
  _candidateBatchRoomId = roomId;
  _candidateBatchRole = role;
  _candidateBatch.push(candidate.toJSON());

  if (_candidateBatchTimer) clearTimeout(_candidateBatchTimer);
  _candidateBatchTimer = setTimeout(() => {
    flushCandidateBatch();
  }, ROOM.ICE_CANDIDATE_BATCH_TIMEOUT_MS);
}

/**
 * Strip null/undefined fields from a candidate object so it passes
 * Firestore rules that use hasOnly() — null values still count as keys.
 */
function cleanCandidate(cand) {
  const cleaned = {};
  for (const [key, val] of Object.entries(cand)) {
    if (val !== null && val !== undefined) {
      cleaned[key] = val;
    }
  }
  return cleaned;
}

/**
 * Flush the pending candidate batch to Firestore as a single document.
 * Called after the batch timeout or when ICE gathering completes.
 * Falls back to individual candidate writes if the batch document is rejected
 * (e.g. deployed rules don't support the batched format).
 */
export async function flushCandidateBatch() {
  if (_candidateBatchTimer) {
    clearTimeout(_candidateBatchTimer);
    _candidateBatchTimer = null;
  }
  if (_candidateBatch.length === 0) return;

  const candidates = _candidateBatch.splice(0);
  const roomId = _candidateBatchRoomId;
  const role = _candidateBatchRole;
  const collName = role === Role.SENDER ? 'callerCandidates' : 'calleeCandidates';
  const collRef = db.collection('rooms').doc(roomId).collection(collName);

  try {
    await collRef.add({ candidates });
  } catch (err) {
    console.warn('[signaling] Batch candidate write failed, falling back to individual writes:', err);
    // Fallback: write each candidate as a separate document in legacy single format
    const writes = candidates.map((cand) => collRef.add(cleanCandidate(cand)));
    try {
      await Promise.all(writes);
    } catch (fallbackErr) {
      console.error('[signaling] Individual candidate fallback also failed:', fallbackErr);
    }
  }
}

/**
 * Listen for the answer SDP (sender waits for receiver's answer).
 * Returns unsubscribe function.
 */
export function listenForAnswer(roomId, onAnswer) {
  const ref = db.collection('rooms').doc(roomId);
  return ref.onSnapshot((snap) => {
    const data = snap.data();
    if (data?.answer) {
      onAnswer(data.answer);
    }
  });
}

/**
 * Listen for the offer SDP (receiver waits for sender's offer on existing room).
 * Returns unsubscribe function.
 */
export function listenForOffer(roomId, onOffer) {
  const ref = db.collection('rooms').doc(roomId);
  return ref.onSnapshot((snap) => {
    const data = snap.data();
    if (data?.offer) {
      onOffer(data.offer);
    }
  });
}

/**
 * Listen for remote ICE candidates.
 * @param {string} roomId
 * @param {'sender'|'receiver'} localRole — our role; we listen for the OTHER side's candidates
 * @param {(candidate: object) => void} onCandidate
 * @returns {() => void} unsubscribe function
 */
export function listenForCandidates(roomId, localRole, onCandidate) {
  const collName = localRole === Role.SENDER ? 'calleeCandidates' : 'callerCandidates';
  return db
    .collection('rooms')
    .doc(roomId)
    .collection(collName)
    .onSnapshot((snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const data = change.doc.data();
          // Support both batched format ({ candidates: [...] }) and legacy single-candidate docs
          if (Array.isArray(data.candidates)) {
            data.candidates.forEach((c) => onCandidate(c));
          } else {
            onCandidate(data);
          }
        }
      });
    });
}

/**
 * Start a heartbeat that periodically updates the room's heartbeat timestamp.
 * Returns a cancel function.
 */
export function startHeartbeat(roomId) {
  const ref = db.collection('rooms').doc(roomId).collection('presence').doc('owner');
  const timer = setInterval(async () => {
    try {
      await ref.set({
        timestamp: firebase.firestore.FieldValue.serverTimestamp(),
      });
    } catch (err) {
      console.warn('[signaling] Heartbeat failed:', err);
    }
  }, ROOM.HEARTBEAT_INTERVAL_MS);

  return () => clearInterval(timer);
}

/**
 * Mark the room as ended (non-destructive departure).
 * Does NOT delete the room — allows the other peer to see "ended" status.
 * Stale rooms will be overwritten by future sessions or cleaned up by TTL.
 */
export async function markRoomEnded(roomId) {
  try {
    await db.collection('rooms').doc(roomId).update({
      status: 'ended',
    });
  } catch (err) {
    // This may fail during unload — that's acceptable.
    // The heartbeat TTL will handle eventual staleness.
    console.warn('[signaling] Failed to mark room ended:', err);
  }
}

/**
 * Best-effort departure: mark room ended, stop heartbeat, unsubscribe listeners.
 * Called during beforeunload — the async Firestore update may or may not complete.
 * If it fails, the heartbeat TTL will eventually mark the room as stale.
 * Idempotent — can be called multiple times safely.
 */
export function depart(roomId) {
  const state = getMutableState();

  // Unsubscribe Firestore listeners
  if (state.roomListener) {
    try {
      state.roomListener();
    } catch {}
    state.roomListener = null;
  }
  if (state.candidatesListener) {
    try {
      state.candidatesListener();
    } catch {}
    state.candidatesListener = null;
  }

  // Stop heartbeat
  if (state.heartbeatTimer) {
    try {
      state.heartbeatTimer();
    } catch {}
    state.heartbeatTimer = null;
  }

  // Best-effort mark room ended (non-blocking, may fail during unload)
  if (roomId) {
    markRoomEnded(roomId).catch(() => {});
  }
}

/**
 * Generate a random owner key for cooperative session identification.
 * This is NOT a security boundary — it is a best-effort hint for room ownership
 * in an authless cooperative model. It cannot prevent a malicious client from
 * overwriting room data.
 */
function generateOwnerKey() {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}
